/** Runtime configuration. The meeting is online-only: it always needs a token endpoint and SFU. */
export interface MeetingConfig {
  appId: string;
  apiBaseUrl: string;
  sfuUrl: string | null;
  /** STUN/TURN servers; the browser needs at least STUN to discover its public candidate. */
  iceServers: RTCIceServer[];
  im: {
    tokenEndpoint: string | null;
    uidEndpoint: string | null;
    fallbackTokenEndpoint: string | null;
  };
  brand: string;
}

type Env = Record<string, string | undefined>;

const trim = (value: string | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed.replace(/\/+$/, '') : null;
};

/**
 * `/livekit` means "same origin, proxied by the app's web server". That keeps one build working for
 * plain HTTP on localhost and for HTTPS on a LAN/Tailscale host (wss://), without mixed content.
 */
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  // The self-hosted LiveKit TURN listener also answers STUN; the public servers are fallback
  // discovery only. Media still flows through the self-hosted LiveKit SFU.
  { urls: 'stun:turn.feiyancloud.com:3478' },
  { urls: 'stun:stun.cloudflare.com:3478' },
  { urls: 'stun:stun.l.google.com:19302' },
];

function parseIceServers(raw: string | undefined): RTCIceServer[] {
  const value = (raw ?? '').trim();
  if (!value) return DEFAULT_ICE_SERVERS;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => {
          if (typeof entry === 'string') return { urls: entry };
          if (entry && typeof entry === 'object' && 'urls' in entry) return entry as RTCIceServer;
          return null;
        })
        .filter((entry): entry is RTCIceServer => entry !== null);
    }
  } catch {
    // Fall through to the comma-separated URL form.
  }
  return value
    .split(',')
    .map((url) => url.trim())
    .filter(Boolean)
    .map((urls) => ({ urls }));
}

function resolveSfuUrl(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return raw;
  if (typeof window === 'undefined') return raw;
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${raw}`;
}

export function loadConfig(env: Env): MeetingConfig {
  return {
    appId: trim(env.VITE_APP_ID) ?? 'demo-app-id',
    apiBaseUrl: trim(env.VITE_API_URL) ?? '',
    sfuUrl: resolveSfuUrl(trim(env.VITE_SFU_URL)),
    iceServers: parseIceServers(env.VITE_ICE_SERVERS),
    im: {
      tokenEndpoint: trim(env.VITE_IM_TOKEN_ENDPOINT),
      uidEndpoint: trim(env.VITE_IM_UID_ENDPOINT),
      fallbackTokenEndpoint: trim(env.VITE_FALLBACK_TOKEN_ENDPOINT),
    },
    brand: trim(env.VITE_BRAND) ?? 'ni-meet',
  };
}

/** Read `?channel=` / `?from=` from an invite link or plain channel string. */
export function parseInvite(input: string): { channel: string | null; from: string | null } {
  const raw = input.trim();
  if (raw.length === 0) return { channel: null, from: null };
  if (!raw.includes('://') && !raw.includes('?') && !raw.includes('&')) {
    return { channel: normalizeChannel(raw), from: null };
  }
  try {
    const url = new URL(raw, 'https://meeting.invalid');
    return {
      channel: normalizeChannel(
        url.searchParams.get('channel') ?? url.searchParams.get('room') ?? '',
      ),
      from: url.searchParams.get('from'),
    };
  } catch {
    return { channel: normalizeChannel(raw), from: null };
  }
}

/** Meeting numbers are channel names: letters, digits, `_` and `-`, max 64 chars. */
export function normalizeChannel(input: string): string | null {
  const cleaned = input
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '');
  if (cleaned.length === 0) return null;
  return cleaned.slice(0, 64);
}

export function buildInviteLink(
  channel: string,
  from?: string,
  origin?: string,
  scene?: string,
): string {
  const base =
    origin ??
    (typeof window !== 'undefined'
      ? window.location.origin + window.location.pathname
      : 'https://meeting.invalid/');
  const url = new URL(base);
  url.searchParams.set('channel', channel);
  if (from) url.searchParams.set('from', from);
  if (scene && scene !== 'meeting') url.searchParams.set('scene', scene);
  return url.toString();
}
